import React, { useState } from 'react';
import { FileText, Plus, Edit2, Eye, Trash2, Download, X, Building, User, Printer, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { generateQuotePDF } from './quotePdfGenerator';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const getCurrencySymbol = (currency) => {
  switch (currency) {
    case 'JPY': return '\u00a5';
    case 'EUR': return '\u20ac';
    case 'USD':
    default: return '$';
  }
};

const formatCurrency = (amount, currency) => {
  const symbol = getCurrencySymbol(currency);
  if (currency === 'JPY') {
    return `${symbol}${Math.round(amount || 0).toLocaleString()}`;
  }
  return `${symbol}${Number(amount || 0).toFixed(2)}`;
};

const SavedQuotesTab = ({ 
  isDark, 
  authFetch, 
  showToast, 
  generatedQuotes,
  setGeneratedQuotes,
  companySettings,
  onEditQuote,
  onDownloadPDF,
  onSetActiveTab
}) => {
  const [previewQuote, setPreviewQuote] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const deleteGeneratedQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      const response = await authFetch(`${API_URL}/api/admin/generated-quotes/${quoteId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setGeneratedQuotes(generatedQuotes.filter(q => q.id !== quoteId));
        showToast('Quote deleted');
      } else {
        showToast('Failed to delete quote', 'error');
      }
    } catch (error) {
      showToast('Error deleting quote', 'error');
    }
  };

  const handleDownloadPDF = async (quote) => {
    if (onDownloadPDF) {
      onDownloadPDF(quote);
    } else {
      setGeneratingPDF(true);
      try {
        await generateQuotePDF(quote, companySettings || {});
        showToast('PDF downloaded');
      } catch (err) {
        showToast('Failed to generate PDF', 'error');
      }
      setGeneratingPDF(false);
    }
  };

  const handlePreviewDownloadPDF = async () => {
    if (!previewQuote) return;
    setGeneratingPDF(true);
    try {
      await generateQuotePDF(previewQuote, companySettings || {});
      showToast('PDF downloaded');
    } catch (err) {
      showToast('Failed to generate PDF', 'error');
    }
    setGeneratingPDF(false);
  };

  // Calculate totals for a quote
  const calcTotals = (quote) => {
    const subtotal = (quote.line_items || []).reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
    const taxAmount = subtotal * ((quote.tax_rate || 0) / 100);
    const shipping = quote.include_shipping !== false ? (quote.shipping_cost || 0) : 0;
    return { subtotal, taxAmount, total: subtotal + shipping + taxAmount };
  };

  return (
    <div className="space-y-4" data-testid="saved-quotes-tab">
      {/* Preview Modal */}
      {previewQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPreviewQuote(null)} />
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-auto rounded-xl p-8" style={{ backgroundColor: '#fff', color: '#000' }}>
            <button onClick={() => setPreviewQuote(null)} className="absolute top-4 right-4 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10" data-testid="close-saved-preview">
              <X className="w-5 h-5" />
            </button>
            
            <div className="quote-preview" style={{ fontFamily: 'Arial, sans-serif' }}>
              {/* Header */}
              <div className="flex justify-between items-start mb-8 pb-4 border-b-2 border-purple-500">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{companySettings?.company_name || 'Company'}</h1>
                  {companySettings?.company_name_local && <p className="text-gray-600">{companySettings.company_name_local}</p>}
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-bold text-purple-600">PROFORMA INVOICE</h2>
                  <p className="text-gray-600">Quote #: {previewQuote.quote_number || previewQuote.id?.slice(-6)}</p>
                  <p className="text-gray-600">Date: {new Date(previewQuote.created_at || Date.now()).toLocaleDateString()}</p>
                </div>
              </div>
              
              {/* Seller / Buyer */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2"><Building className="w-4 h-4" /> SELLER</h3>
                  <p className="font-semibold">{companySettings?.company_name || ''}</p>
                  <p>{companySettings?.contact_person || ''}</p>
                  <p>{companySettings?.address || ''}</p>
                  <p>Tel: {companySettings?.phone || ''}</p>
                  <p>Email: {companySettings?.email || ''}</p>
                  {companySettings?.whatsapp && <p>WhatsApp: {companySettings.whatsapp}</p>}
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2"><User className="w-4 h-4" /> BUYER</h3>
                  <p className="font-semibold">{previewQuote.customer_company || previewQuote.customer_name}</p>
                  <p>{previewQuote.customer_contact}</p>
                  <p>{previewQuote.customer_address}</p>
                  <p>Tel: {previewQuote.customer_phone}</p>
                  <p>Email: {previewQuote.customer_email}</p>
                  {previewQuote.customer_whatsapp && <p>WhatsApp: {previewQuote.customer_whatsapp}</p>}
                </div>
              </div>
              
              {/* Line Items */}
              <table className="w-full mb-8 border-collapse">
                <thead>
                  <tr className="bg-purple-600 text-white">
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Item</th>
                    <th className="p-3 text-left">Description</th>
                    <th className="p-3 text-center">Qty</th>
                    <th className="p-3 text-right">Unit Price</th>
                    <th className="p-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(previewQuote.line_items || []).map((item, idx) => (
                    <tr key={item.id || idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b">{item.item_type}</td>
                      <td className="p-3 border-b font-medium">{item.item_name}</td>
                      <td className="p-3 border-b text-sm text-gray-600 whitespace-pre-wrap">{item.description}</td>
                      <td className="p-3 border-b text-center">{item.quantity}</td>
                      <td className="p-3 border-b text-right">{formatCurrency(item.unit_price, previewQuote.currency)}</td>
                      <td className="p-3 border-b text-right font-medium">{formatCurrency((item.quantity || 0) * (item.unit_price || 0), previewQuote.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Product Images */}
              {previewQuote.images && previewQuote.images.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-bold text-gray-700 mb-4">PRODUCT IMAGES</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {previewQuote.images.map((img, idx) => (
                      <div key={img.id || idx} className="border rounded-lg overflow-hidden bg-gray-50">
                        <img 
                          src={img.url?.startsWith('/') ? `${API_URL}${img.url}` : img.url} 
                          alt={img.name || `Product ${idx + 1}`}
                          className="w-full h-32 object-cover"
                          onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 128"><rect fill="%23f3f4f6" width="150" height="128"/><text x="75" y="64" text-anchor="middle" fill="%239ca3af" font-size="12">No Image</text></svg>'; }}
                        />
                        <p className="text-xs text-center p-2 text-gray-600 truncate">{img.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Totals */}
              {(() => {
                const t = calcTotals(previewQuote);
                return (
                  <div className="flex justify-end mb-8">
                    <div className="w-64">
                      <div className="flex justify-between py-2 border-b"><span>Subtotal:</span><span>{formatCurrency(t.subtotal, previewQuote.currency)}</span></div>
                      <div className="flex justify-between py-2 border-b">
                        <span>Shipping:</span>
                        <span>{previewQuote.include_shipping !== false ? formatCurrency(previewQuote.shipping_cost, previewQuote.currency) : 'Not Included'}</span>
                      </div>
                      {(previewQuote.tax_rate || 0) > 0 && <div className="flex justify-between py-2 border-b"><span>Tax ({previewQuote.tax_rate}%):</span><span>{formatCurrency(t.taxAmount, previewQuote.currency)}</span></div>}
                      <div className="flex justify-between py-3 text-lg font-bold text-purple-600"><span>TOTAL:</span><span>{formatCurrency(t.total, previewQuote.currency)} {previewQuote.currency}</span></div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Quote Options */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-4 bg-purple-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Installation</p>
                  <p className="font-semibold text-purple-700">{previewQuote.installation_location === 'inside' ? 'Indoor' : previewQuote.installation_location === 'outside' ? 'Outdoor' : 'N/A'}</p>
                </div>
                {previewQuote.mounting_style && <div className="text-center"><p className="text-xs text-gray-500 uppercase">Mounting</p><p className="font-semibold text-purple-700">{previewQuote.mounting_style}</p></div>}
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Warranty</p>
                  <p className="font-semibold text-purple-700">{previewQuote.include_warranty ? '1 Year' : 'Not Included'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Shipping</p>
                  <p className="font-semibold text-purple-700">{previewQuote.include_shipping !== false ? 'Included' : 'Not Included'}</p>
                </div>
              </div>
              
              {/* Delivery & Payment */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">DELIVERY INFORMATION</h3>
                  <p><strong>Processing:</strong> {previewQuote.processing_time || 'TBD'}</p>
                  <p><strong>Delivery:</strong> {previewQuote.delivery_time || 'TBD'}</p>
                  <p><strong>Method:</strong> {previewQuote.delivery_method || 'TBD'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">PAYMENT TERMS</h3>
                  <p>{previewQuote.payment_terms || 'TBD'}</p>
                  {companySettings?.paypal_email && <p className="mt-2"><strong>PayPal:</strong> {companySettings.paypal_email}</p>}
                  {companySettings?.bank_details && <p className="mt-2 text-sm whitespace-pre-wrap">{companySettings.bank_details}</p>}
                </div>
              </div>
              
              {previewQuote.notes && (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">NOTES</h3>
                  <p className="whitespace-pre-wrap">{previewQuote.notes}</p>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-4 mt-6 pt-4 border-t">
              <Button onClick={handlePreviewDownloadPDF} disabled={generatingPDF} className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="preview-download-pdf">
                {generatingPDF ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Download className="w-4 h-4" /> Download PDF</>}
              </Button>
              <Button onClick={() => { onEditQuote(previewQuote); setPreviewQuote(null); }} className="flex-1 flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white" data-testid="preview-edit-btn">
                <Edit2 className="w-4 h-4" /> Edit Quote
              </Button>
              <Button onClick={() => setPreviewQuote(null)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <p style={{ color: isDark ? '#666' : '#94a3b8' }}>
            {generatedQuotes.length} saved quotes
          </p>
        </div>
        <Button
          onClick={() => onSetActiveTab('quote-generator')}
          className="flex items-center gap-2"
          style={{
            background: isDark 
              ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
              : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)'
          }}
        >
          <Plus className="w-4 h-4" /> Create New Quote
        </Button>
      </div>

      {generatedQuotes.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: isDark ? '#333' : '#e2e8f0' }} />
          <p style={{ color: isDark ? '#666' : '#94a3b8' }}>No saved quotes yet. Create your first quote!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {generatedQuotes.map((quote) => (
            <div 
              key={quote.id}
              className="p-4 rounded-lg transition-all hover:shadow-lg cursor-pointer"
              style={{
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
              }}
              onClick={() => setPreviewQuote(quote)}
              data-testid={`quote-card-${quote.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-lg font-bold" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                      #{quote.quote_number || quote.id?.slice(-6)}
                    </span>
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: isDark ? 'rgba(0, 255, 136, 0.2)' : 'rgba(16, 185, 129, 0.2)', color: isDark ? '#00ff88' : '#10b981' }}>
                      {formatCurrency(quote.total, quote.currency)}
                    </span>
                    {quote.quote_request_id && (
                      <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: isDark ? 'rgba(255, 255, 0, 0.2)' : 'rgba(234, 179, 8, 0.2)', color: isDark ? '#ffff00' : '#ca8a04' }}>
                        Linked to Request
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                    {quote.customer_name || 'Unnamed Customer'}
                  </h3>
                  {quote.customer_company && (
                    <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>{quote.customer_company}</p>
                  )}
                  <p className="text-sm mt-1" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                    {quote.line_items?.length || 0} items - {quote.customer_email || 'No email'}
                  </p>
                  <p className="text-xs mt-2" style={{ color: isDark ? '#444' : '#cbd5e1' }}>
                    Created: {new Date(quote.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onEditQuote(quote)}
                    className="p-2 rounded-lg hover:bg-white/10"
                    style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}
                    title="Edit Quote"
                    data-testid={`edit-quote-${quote.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewQuote(quote)}
                    className="p-2 rounded-lg hover:bg-white/10"
                    style={{ color: isDark ? '#00ff88' : '#10b981' }}
                    title="Preview Quote"
                    data-testid={`preview-quote-${quote.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDownloadPDF(quote)}
                    className="p-2 rounded-lg hover:bg-white/10"
                    style={{ color: isDark ? '#ff00ff' : '#d946ef' }}
                    title="Download PDF"
                    data-testid={`download-pdf-${quote.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteGeneratedQuote(quote.id)}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-red-500"
                    title="Delete Quote"
                    data-testid={`delete-quote-${quote.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedQuotesTab;
